import type { ErrorInfo } from "react";
import React from "react";

import type { Atom } from "jotai";
import { atom } from "jotai";
import { atomEffect } from "jotai-effect";

import { UnsubscribeFunctionsManagerDelegate } from "@lib/utils/UnsubscribeFunctionsManagerDelegate";

import type { AtomStore } from "./AtomStoreMaster";
import type { InitialSettings } from "./InitialSettings";
import type { Dashboard } from "./internal/Dashboard";
import { ChannelManager, ChannelManagerNotificationTopic } from "./internal/DataChannels/ChannelManager";
import { ModuleInstanceSerializer } from "./internal/ModuleInstanceSerializer";
import { ModuleInstanceStatusControllerInternal } from "./internal/ModuleInstanceStatusControllerInternal";
import type {
    ImportStatus,
    Module,
    ModuleInterfaceTypes,
    ModuleSettings,
    ModuleComponentsStateBase,
    ModuleView,
    ModuleComponentSerializationFunctions,
} from "./Module";
import { ModuleContext } from "./ModuleContext";
import type { SerializedModuleInstanceState } from "./ModuleInstance.schema";
import type { SyncSettingKey } from "./SyncSettings";
import type { ChannelDefinition, ChannelReceiverDefinition } from "./types/dataChannnel";
import type { InterfaceInitialization } from "./UniDirectionalModuleComponentsInterface";
import { UniDirectionalModuleComponentsInterface } from "./UniDirectionalModuleComponentsInterface";

export enum ModuleInstanceLifeCycleState {
    INITIALIZING,
    OK,
    ERROR,
    RESETTING,
}

export enum ModuleInstanceTopic {
    TITLE = "title",
    SYNCED_SETTINGS = "synced-settings",
    LIFECYCLE_STATE = "state",
    IMPORT_STATUS = "import-status",
    SERIALIZED_STATE = "serialized-state",
    HAS_INVALID_PERSISTED_SETTINGS = "has-invalid-persisted-settings",
    HAS_INVALID_PERSISTED_VIEW = "has-invalid-persisted-view",
}

export type ModuleInstanceTopicValueTypes = {
    [ModuleInstanceTopic.TITLE]: string;
    [ModuleInstanceTopic.SYNCED_SETTINGS]: SyncSettingKey[];
    [ModuleInstanceTopic.LIFECYCLE_STATE]: ModuleInstanceLifeCycleState;
    [ModuleInstanceTopic.IMPORT_STATUS]: ImportStatus;
    [ModuleInstanceTopic.SERIALIZED_STATE]: void;
    [ModuleInstanceTopic.HAS_INVALID_PERSISTED_SETTINGS]: boolean;
    [ModuleInstanceTopic.HAS_INVALID_PERSISTED_VIEW]: boolean;
};

export interface ModuleInstanceOptions<
    TInterfaceTypes extends ModuleInterfaceTypes,
    TSerializedState extends ModuleComponentsStateBase,
> {
    module: Module<TInterfaceTypes, TSerializedState>;
    atomStore: AtomStore;
    id: string;
    channelDefinitions: ChannelDefinition[] | null;
    channelReceiverDefinitions: ChannelReceiverDefinition[] | null;
}

export type PartialSerializedModuleState<T extends ModuleComponentsStateBase> = {
    settings?: Partial<T["settings"]>;
    view?: Partial<T["view"]>;
};

export class ModuleInstance<
    TInterfaceTypes extends ModuleInterfaceTypes,
    TSerializedStateSchema extends ModuleComponentsStateBase,
> {
    private _unsubscribeFunctionsManagerDelegate = new UnsubscribeFunctionsManagerDelegate();

    private _id: string;
    private _title: string;
    private _initialized: boolean = false;
    private _moduleInstanceState: ModuleInstanceLifeCycleState = ModuleInstanceLifeCycleState.INITIALIZING;
    private _fatalError: { err: Error; errInfo: ErrorInfo } | null = null;
    private _syncedSettingKeys: SyncSettingKey[] = [];
    private _module: Module<TInterfaceTypes, TSerializedStateSchema>;
    private _context: ModuleContext<TInterfaceTypes> | null = null;
    private _subscribers: Map<keyof ModuleInstanceTopicValueTypes, Set<() => void>> = new Map();
    private _initialSettings: InitialSettings | null = null;
    private _statusController: ModuleInstanceStatusControllerInternal = new ModuleInstanceStatusControllerInternal();

    // ChannelManager should be elevated to Dashboard level and shared among module instances in the dashboard
    private _channelManager: ChannelManager;

    private _settingsToViewInterface: UniDirectionalModuleComponentsInterface<
        Exclude<TInterfaceTypes["settingsToView"], undefined>
    > | null = null;
    private _viewToSettingsInterface: UniDirectionalModuleComponentsInterface<
        Exclude<TInterfaceTypes["viewToSettings"], undefined>
    > | null = null;
    private _settingsToViewInterfaceEffectsAtom: Atom<void> | null = null;
    private _viewToSettingsInterfaceEffectsAtom: Atom<void> | null = null;
    private _atomStore: AtomStore;

    private _dashboard: Dashboard | null = null;
    private _serializer: ModuleInstanceSerializer<TSerializedStateSchema> | null = null;
    private _storedSerializedState: SerializedModuleInstanceState | null = null;
    private _storedTemplateState: PartialSerializedModuleState<TSerializedStateSchema> | null = null;

    private _hasInvalidPersistedSettings: boolean = false;
    private _hasInvalidPersistedView: boolean = false;

    constructor(options: ModuleInstanceOptions<TInterfaceTypes, TSerializedStateSchema>) {
        this._id = options.id;
        this._title = options.module.getDefaultTitle();
        this._module = options.module;
        this._atomStore = options.atomStore;

        this._channelManager = new ChannelManager(this._id);

        if (options.channelReceiverDefinitions) {
            this._channelManager.registerReceivers(
                options.channelReceiverDefinitions.map((el) => ({
                    ...el,
                    supportsMultiContents: el.supportsMultiContents ?? false,
                })),
            );
        }

        if (options.channelDefinitions) {
            this._channelManager.registerChannels(options.channelDefinitions);
        }

        this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
            "channel-manager",
            this._channelManager
                .getPublishSubscribeDelegate()
                .subscribe(
                    ChannelManagerNotificationTopic.CONNECTION_STATE_REVISION,
                    this.handleStateChange.bind(this),
                ),
        );
    }

    serializeState(): SerializedModuleInstanceState {
        // Not applied yet (module code still loading, or failed to load) - serializing the live, still
        // empty state would silently drop it
        if (this._storedSerializedState) {
            return this._storedSerializedState;
        }

        return {
            id: this._id,
            name: this._module.getName(),
            dataChannelManagerState: this._channelManager.serializeState(),
            syncedSettingKeys: this._syncedSettingKeys,
            serializedState: this._serializer?.getStringifiedSerializedState() ?? null,
        };
    }

    initiateDeserialization(raw: SerializedModuleInstanceState, dashboard: Dashboard): void {
        this._storedSerializedState = raw;
        this._dashboard = dashboard;
        this.deserializeState();
    }

    initiateTemplateStateApplication(initialState: PartialSerializedModuleState<TSerializedStateSchema>): void {
        this._storedTemplateState = initialState;
        this.applyTemplateState();
    }

    private applyTemplateState(): void {
        if (this._initialized && this._storedTemplateState && this._serializer) {
            this._serializer.applyTemplateState(this._storedTemplateState);
            this._storedTemplateState = null;
        }
    }

    private deserializeState(): void {
        if (this._initialized && this._storedSerializedState && this._dashboard) {
            this._syncedSettingKeys = this._storedSerializedState.syncedSettingKeys;

            this._id = this._storedSerializedState.id;

            if (this._storedSerializedState.serializedState && this._serializer) {
                const result = this._serializer.deserializeState(this._storedSerializedState.serializedState);
                if (result) {
                    this.setInvalidPersistedSettingsFlag(!result.settingsStateApplied);
                    this.setInvalidPersistedViewFlag(!result.viewStateApplied);
                }
            }

            this._channelManager.deserializeState(
                this._storedSerializedState.dataChannelManagerState,
                this._dashboard.getModuleInstances(),
            );

            this._storedSerializedState = null;
        }
    }

    hasInvalidPersistedSettings(): boolean {
        return this._hasInvalidPersistedSettings;
    }

    hasInvalidPersistedView(): boolean {
        return this._hasInvalidPersistedView;
    }

    private setInvalidPersistedSettingsFlag(value: boolean): void {
        this._hasInvalidPersistedSettings = value;
        this.notifySubscribers(ModuleInstanceTopic.HAS_INVALID_PERSISTED_SETTINGS);
    }

    private setInvalidPersistedViewFlag(value: boolean): void {
        this._hasInvalidPersistedView = value;
        this.notifySubscribers(ModuleInstanceTopic.HAS_INVALID_PERSISTED_VIEW);
    }

    resetInvalidPersistedFlags(): void {
        this.setInvalidPersistedSettingsFlag(false);
        this.setInvalidPersistedViewFlag(false);
    }

    getUniDirectionalSettingsToViewInterface(): UniDirectionalModuleComponentsInterface<
        Exclude<TInterfaceTypes["settingsToView"], undefined>
    > {
        if (!this._settingsToViewInterface) {
            throw `Module instance '${this._title}' does not have an interface yet. Did you forget to init the module?`;
        }
        return this._settingsToViewInterface;
    }

    getUniDirectionalViewToSettingsInterface(): UniDirectionalModuleComponentsInterface<
        Exclude<TInterfaceTypes["viewToSettings"], undefined>
    > {
        if (!this._viewToSettingsInterface) {
            throw `Module instance '${this._title}' does not have an interface yet. Did you forget to init the module?`;
        }
        return this._viewToSettingsInterface;
    }

    getChannelManager(): ChannelManager {
        return this._channelManager;
    }

    initialize(): void {
        this._context = new ModuleContext<TInterfaceTypes>(this);
        this._initialized = true;
        this.setModuleInstanceState(ModuleInstanceLifeCycleState.OK);
        this.deserializeState();
        this.applyTemplateState();
    }

    makeSerializer(serializationFunctions: ModuleComponentSerializationFunctions<TSerializedStateSchema> | null): void {
        if (serializationFunctions) {
            this._serializer = new ModuleInstanceSerializer<TSerializedStateSchema>(
                this,
                this._atomStore,
                this._module.getSerializedStateSchema(),
                serializationFunctions,
                this.handleStateChange.bind(this),
            );
        }
    }

    handleStateChange(): void {
        this.notifySubscribers(ModuleInstanceTopic.SERIALIZED_STATE);
    }

    makeSettingsToViewInterface(
        interfaceInitialization: InterfaceInitialization<Exclude<TInterfaceTypes["settingsToView"], undefined>>,
    ) {
        if (!interfaceInitialization) {
            return;
        }
        this._settingsToViewInterface = new UniDirectionalModuleComponentsInterface(interfaceInitialization);
    }

    makeViewToSettingsInterface(
        interfaceInitialization: InterfaceInitialization<Exclude<TInterfaceTypes["viewToSettings"], undefined>>,
    ) {
        if (!interfaceInitialization) {
            return;
        }
        this._viewToSettingsInterface = new UniDirectionalModuleComponentsInterface(interfaceInitialization);
    }

    makeSettingsToViewInterfaceEffectsAtom(): void {
        const effectFuncs = this.getModule().getSettingsToViewInterfaceEffects();
        const getUniDirectionalSettingsToViewInterface: () => UniDirectionalModuleComponentsInterface<
            Exclude<TInterfaceTypes["settingsToView"], undefined>
        > = () => this.getUniDirectionalSettingsToViewInterface();

        const newEffects: Atom<void>[] = [];
        for (const effectFunc of effectFuncs) {
            const effect = atomEffect((get, set) => {
                function getAtomFromInterface<TKey extends keyof Exclude<TInterfaceTypes["settingsToView"], undefined>>(
                    key: TKey,
                ): Exclude<TInterfaceTypes["settingsToView"], undefined>[TKey] {
                    return get(getUniDirectionalSettingsToViewInterface().getAtom(key));
                }
                effectFunc(getAtomFromInterface, set, get);
            });
            newEffects.push(effect);
        }
        this._settingsToViewInterfaceEffectsAtom = atom((get) => {
            for (const effect of newEffects) {
                get(effect);
            }
        });
    }

    makeViewToSettingsInterfaceEffectsAtom(): void {
        const effectFuncs = this.getModule().getViewToSettingsInterfaceEffects();
        const getUniDirectionalViewToSettingsInterface: () => UniDirectionalModuleComponentsInterface<
            Exclude<TInterfaceTypes["viewToSettings"], undefined>
        > = () => this.getUniDirectionalViewToSettingsInterface();

        const newEffects: Atom<void>[] = [];
        for (const effectFunc of effectFuncs) {
            const effect = atomEffect((get, set) => {
                function getAtomFromInterface<TKey extends keyof Exclude<TInterfaceTypes["viewToSettings"], undefined>>(
                    key: TKey,
                ): Exclude<TInterfaceTypes["viewToSettings"], undefined>[TKey] {
                    return get(getUniDirectionalViewToSettingsInterface().getAtom(key));
                }
                effectFunc(getAtomFromInterface, set, get);
            });
            newEffects.push(effect);
        }
        this._viewToSettingsInterfaceEffectsAtom = atom((get) => {
            for (const effect of newEffects) {
                get(effect);
            }
        });
    }

    getSettingsToViewInterfaceEffectsAtom(): Atom<void> {
        if (!this._settingsToViewInterfaceEffectsAtom) {
            throw `Module instance '${this._title}' does not have settings to view interface effects yet. Did you forget to init the module?`;
        }
        return this._settingsToViewInterfaceEffectsAtom;
    }

    getViewToSettingsInterfaceEffectsAtom(): Atom<void> {
        if (!this._viewToSettingsInterfaceEffectsAtom) {
            throw `Module instance '${this._title}' does not have view to settings interface effects yet. Did you forget to init the module?`;
        }
        return this._viewToSettingsInterfaceEffectsAtom;
    }

    addSyncedSetting(settingKey: SyncSettingKey): void {
        this._syncedSettingKeys.push(settingKey);
        this.notifySubscribers(ModuleInstanceTopic.SYNCED_SETTINGS);
        this.handleStateChange();
    }

    getSyncedSettingKeys(): SyncSettingKey[] {
        return this._syncedSettingKeys;
    }

    isSyncedSetting(settingKey: SyncSettingKey): boolean {
        return this._syncedSettingKeys.includes(settingKey);
    }

    removeSyncedSetting(settingKey: SyncSettingKey): void {
        this._syncedSettingKeys = this._syncedSettingKeys.filter((a) => a !== settingKey);
        this.notifySubscribers(ModuleInstanceTopic.SYNCED_SETTINGS);
        this.handleStateChange();
    }

    isInitialized(): boolean {
        return this._initialized;
    }

    getViewFC(): ModuleView<TInterfaceTypes> {
        return this._module.viewFC;
    }

    getSettingsFC(): ModuleSettings<TInterfaceTypes> {
        return this._module.settingsFC;
    }

    getImportState(): ImportStatus {
        return this._module.getImportState();
    }

    getContext(): ModuleContext<TInterfaceTypes> {
        if (!this._context) {
            throw `Module context is not available yet. Did you forget to init the module '${this._title}.'?`;
        }
        return this._context;
    }

    getId(): string {
        return this._id;
    }

    getName(): string {
        return this._module.getName();
    }

    getTitle(): string {
        return this._title;
    }

    setTitle(title: string): void {
        this._title = title;
        this.notifySubscribers(ModuleInstanceTopic.TITLE);
    }

    notifySubscribers(topic: ModuleInstanceTopic): void {
        const subscribers = this._subscribers.get(topic);
        if (subscribers) {
            subscribers.forEach((subscriber) => {
                subscriber();
            });
        }
    }

    makeSubscriberFunction(topic: ModuleInstanceTopic): (onStoreChangeCallback: () => void) => () => void {
        // Using arrow function in order to keep "this" in context
        const subscriber = (onStoreChangeCallback: () => void): (() => void) => {
            const subscribers = this._subscribers.get(topic) || new Set();
            subscribers.add(onStoreChangeCallback);
            this._subscribers.set(topic, subscribers);

            return () => {
                subscribers.delete(onStoreChangeCallback);
            };
        };

        return subscriber;
    }

    makeSnapshotGetter<T extends ModuleInstanceTopic>(topic: T): () => ModuleInstanceTopicValueTypes[T] {
        const snapshotGetter = (): any => {
            if (topic === ModuleInstanceTopic.TITLE) {
                return this.getTitle();
            }
            if (topic === ModuleInstanceTopic.SYNCED_SETTINGS) {
                return this.getSyncedSettingKeys();
            }
            if (topic === ModuleInstanceTopic.LIFECYCLE_STATE) {
                return this.getModuleInstanceState();
            }
            if (topic === ModuleInstanceTopic.IMPORT_STATUS) {
                return this.getImportState();
            }
            if (topic === ModuleInstanceTopic.SERIALIZED_STATE) {
                return;
            }
            if (topic === ModuleInstanceTopic.HAS_INVALID_PERSISTED_SETTINGS) {
                return this.hasInvalidPersistedSettings();
            }
            if (topic === ModuleInstanceTopic.HAS_INVALID_PERSISTED_VIEW) {
                return this.hasInvalidPersistedView();
            }
            throw `Unsupported topic '${topic}' in ModuleInstance snapshot getter.`;
        };

        return snapshotGetter;
    }

    getModule(): Module<TInterfaceTypes, TSerializedStateSchema> {
        return this._module;
    }

    getStatusController(): ModuleInstanceStatusControllerInternal {
        return this._statusController;
    }

    private setModuleInstanceState(moduleInstanceState: ModuleInstanceLifeCycleState): void {
        this._moduleInstanceState = moduleInstanceState;
        this.notifySubscribers(ModuleInstanceTopic.LIFECYCLE_STATE);
    }

    getModuleInstanceState(): ModuleInstanceLifeCycleState {
        return this._moduleInstanceState;
    }

    setFatalError(err: Error, errInfo: ErrorInfo): void {
        this._fatalError = {
            err,
            errInfo,
        };
        this.setModuleInstanceState(ModuleInstanceLifeCycleState.ERROR);
    }

    getFatalError(): {
        err: Error;
        errInfo: ErrorInfo;
    } | null {
        return this._fatalError;
    }

    reset(): Promise<void> {
        this.setModuleInstanceState(ModuleInstanceLifeCycleState.RESETTING);

        return new Promise((resolve) => {
            this._module.notifyInstanceUnload(this._id);

            this.initialize();
            resolve();
        });
    }

    setInitialSettings(initialSettings: InitialSettings): void {
        this._initialSettings = initialSettings;
    }

    getInitialSettings(): InitialSettings | null {
        return this._initialSettings;
    }

    unload(): void {
        this._module.notifyInstanceUnload(this._id);
        this._channelManager.unregisterAllChannels();
        this._channelManager.unregisterAllReceivers();
        this._unsubscribeFunctionsManagerDelegate.unsubscribeAll();
        this._context = null;
        this._settingsToViewInterface = null;
        this._viewToSettingsInterface = null;
        this._settingsToViewInterfaceEffectsAtom = null;
        this._viewToSettingsInterfaceEffectsAtom = null;
        this._serializer?.beforeDestroy();
        this._module.removeInstance(this);
    }
}

export function useModuleInstanceTopicValue<T extends ModuleInstanceTopic>(
    moduleInstance: ModuleInstance<any, any>,
    topic: T,
): ModuleInstanceTopicValueTypes[T] {
    const value = React.useSyncExternalStore<ModuleInstanceTopicValueTypes[T]>(
        moduleInstance.makeSubscriberFunction(topic),
        moduleInstance.makeSnapshotGetter(topic),
    );
    return value;
}
